// components/HomePage.js - Page d'accueil mobile-first iOS-like avec design moderne
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE, SUPER_ADMIN_EMAIL } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { useAuth } from '../App';
import LanguageSelector from './LanguageSelector';
import { NotificationIcon } from './Notifications/NotificationIcon';
import {
  Clock, Calendar, DollarSign, Users, BarChart3, Wallet, BookOpen,
  File, MessageSquare, TrendingUp, HelpCircle, User,
  Home, Plus, LogOut, CreditCard, Sparkles, Camera, History, QrCode, MapPin, Shield, Lightbulb, CalendarDays, Target, Menu, X, PanelRightOpen, MessageCircle, Radio
} from 'lucide-react';

// Fallback when API not loaded: show only Starter plan modules (no Performance, Ideas, Announcements)
const DEFAULT_PLAN_FEATURES = ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq'];

const hasFeature = (enabledFeatures, feature, billingLoaded = true) => {
  if (!billingLoaded) return false; // Don't show modules until plan is loaded
  const list = (enabledFeatures && enabledFeatures.length > 0) ? enabledFeatures : DEFAULT_PLAN_FEATURES;
  return list.includes(feature);
};

const HomePage = ({ user, onNavigate, onLogout }) => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-GB';
  const { refreshUser, dashboardStats = {}, setDashboardStats } = useAuth();
  const navigate = useNavigate();
  const { enabledFeatures = [], billingLoaded = false, onRefetchBilling, notificationSocket = null } = useOutletContext() || {};
  const isSuperAdmin = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin';
  const [currentTime, setCurrentTime] = useState(new Date());

  // Company + subscription: refetch billing (company role, subscription plan) on mount
  useEffect(() => {
    if (refreshUser && user) refreshUser();
    if (onRefetchBilling) onRefetchBilling();
  }, [onRefetchBilling]);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState(() => ({
    leaveDays: dashboardStats.leaveDays ?? 0,
    hoursWorked: dashboardStats.hoursWorked ?? 0,
    attendanceRate: dashboardStats.attendanceRate ?? 0,
    unreadLeaves: 0,
    unreadRequests: 0,
    unreadFormations: 0,
    unreadExpenses: 0,
    unreadMessages: 0,
    unreadIdeas: 0,
    pendingFormationRegistrations: 0,
    unreadDocuments: 0,
    unreadPlanning: 0,
    unreadPerformance: 0
  }));
  const [chatUnreadTotal, setChatUnreadTotal] = useState(0);
  const [assignedToMeLeaves, setAssignedToMeLeaves] = useState([]); // Liste des demandes "assignées à moi" (pour tooltip + debug)
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    updateTime();
    loadAttendanceStatus();
    loadStats();
    loadTodayAttendance();
    
    // Détecter la taille de l'écran pour mobile (drawer sur tablette étroite aussi)
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Real-time: Expense Reports tile badge when new expense notification arrives
  useEffect(() => {
    if (!notificationSocket) return;
    const onNotification = (payload) => {
      if (payload && payload.type === 'expense') loadStats();
    };
    notificationSocket.on('notification:new', onNotification);
    return () => notificationSocket.off('notification:new', onNotification);
  }, [notificationSocket]);

  // Refetch stats when notification-refresh is dispatched (e.g. after opening bell)
  useEffect(() => {
    const onRefresh = () => loadStats();
    window.addEventListener('notification-refresh', onRefresh);
    return () => window.removeEventListener('notification-refresh', onRefresh);
  }, []);

  useEffect(() => {
    if (!isMobile && mobileDrawerOpen) setMobileDrawerOpen(false);
  }, [isMobile, mobileDrawerOpen]);

  // Live Chat unread badge: fetch on load + real-time via event (event can carry total from layout).
  const liveChatEnabled = hasFeature(enabledFeatures, 'live_chat', billingLoaded);
  useEffect(() => {
    if (!liveChatEnabled) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const fetchChatUnread = () => {
      fetch(`${API_BASE}/api/chat/unread-total`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => {
          const total = d?.total;
          if (d?.success && (typeof total === 'number' || typeof total === 'string')) setChatUnreadTotal(Number(total) || 0);
        })
        .catch(() => {});
    };
    fetchChatUnread();
    const onRefresh = (e) => {
      if (e?.detail?.total !== undefined && e.detail !== null) setChatUnreadTotal(Number(e.detail.total) || 0);
      else fetchChatUnread();
    };
    window.addEventListener('chat-unread-refresh', onRefresh);
    return () => window.removeEventListener('chat-unread-refresh', onRefresh);
  }, [liveChatEnabled]);

  const updateTime = () => {
    setCurrentTime(new Date());
  };

  const loadAttendanceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/attendance/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceStatus(data);
      }
    } catch (error) {
      console.error('Erreur chargement statut:', error);
    }
  };

  const loadTodayAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API_BASE}/api/attendance/history?startDate=${today}&endDate=${today}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.records && data.records.length > 0) {
          const firstRecord = data.records[0];
          setTodayAttendance({
            clockIn: firstRecord.clock_in_time || firstRecord.timestamp,
            type: firstRecord.type
          });
        }
      }
    } catch (error) {
      console.error('Erreur chargement pointage du jour:', error);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const isAdminOrManager = ['admin', 'manager'].includes(user?.role);

      // Expense pending-count only for admin (badge shows only to admin; manager has no validator role)
      const fetches = [
        fetch(`${API_BASE}/api/leaves/my-balances`, { headers }),
        fetch(`${API_BASE}/api/attendance/my-dashboard-stats`, { headers }),
        fetch(`${API_BASE}/api/leaves/my-requests?status=pending`, { headers }),
        fetch(`${API_BASE}/api/formations/unread-count`, { headers }),
        user?.role === 'admin' ? fetch(`${API_BASE}/api/notes-frais/pending-count`, { headers }).catch(() => null) : Promise.resolve(null),
        fetch(`${API_BASE}/api/announcements/unread`, { headers }).catch(() => null)
      ];
      if (hasFeature(enabledFeatures, 'ideas', billingLoaded)) {
        fetches.push(fetch(`${API_BASE}/api/ideas/pending-count`, { headers }).catch(() => null));
      }
      if (isAdminOrManager) {
        fetches.push(fetch(`${API_BASE}/api/leaves/assigned-to-me`, { headers }));
        if (hasFeature(enabledFeatures, 'formations', billingLoaded)) {
          fetches.push(fetch(`${API_BASE}/api/formations/pending-registrations-count`, { headers }).catch(() => null));
        }
      }

      const results = await Promise.all(fetches);
      const leavesRes = results[0];
      const attendanceStatsRes = results[1];
      const requestsRes = results[2];
      const formationsRes = results[3];
      const expensesRes = results[4];
      const messagesRes = results[5];
      const ideasRes = hasFeature(enabledFeatures, 'ideas', billingLoaded) ? results[6] : null;
      const assignedToMeRes = isAdminOrManager ? results[hasFeature(enabledFeatures, 'ideas', billingLoaded) ? 7 : 6] : null;
      const formationsPendingRes = isAdminOrManager && hasFeature(enabledFeatures, 'formations', billingLoaded)
        ? results[hasFeature(enabledFeatures, 'ideas', billingLoaded) ? 8 : 7]
        : null;

      const updates = {};

      if (leavesRes.ok) {
        const leavesData = await leavesRes.json();
        if (leavesData.success) {
          if (leavesData.paid_remaining != null && leavesData.paid_remaining !== undefined) {
            updates.leaveDays = Math.round(Number(leavesData.paid_remaining));
          } else if (leavesData.total_remaining != null && leavesData.total_remaining !== undefined) {
            updates.leaveDays = Math.round(Number(leavesData.total_remaining));
          } else if (leavesData.balances?.length > 0) {
            const paidSum = leavesData.balances
              .filter(b => b.is_paid === true || b.is_paid === 't')
              .reduce((sum, b) => sum + Number(b.remaining_days || 0), 0);
            updates.leaveDays = Math.round(paidSum || leavesData.balances.reduce((sum, b) => sum + Number(b.remaining_days || 0), 0));
          } else {
            updates.leaveDays = 0;
          }
        }
      }
      if (attendanceStatsRes?.ok) {
        try {
          const attData = await attendanceStatsRes.json();
          if (attData.success) {
            if (typeof attData.hoursWorked === 'number') updates.hoursWorked = attData.hoursWorked;
            if (typeof attData.attendanceRate === 'number') updates.attendanceRate = attData.attendanceRate;
          }
        } catch (e) {}
      }
      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        const pendingCount = (requestsData.success && Array.isArray(requestsData.requests)) ? requestsData.requests.length : 0;
        updates.unreadRequests = pendingCount;
      }
      if (isAdminOrManager && assignedToMeRes?.ok) {
        const assignedData = await assignedToMeRes.json();
        const list = (assignedData.success && Array.isArray(assignedData.requests)) ? assignedData.requests : [];
        const assignedCount = list.length;
        updates.unreadLeaves = assignedCount;
        setAssignedToMeLeaves(list);
        // Debug: batao kon si demandes badge me aa rahi hain
        if (list.length > 0) {
          console.log(`📋 Leaves badge (${list.length}) – yeh demandes "Assignées à moi" hain:`, list.map((r, i) => ({
            no: i + 1,
            id: r.id,
            employee: r.employee_name,
            type: r.leave_type_name,
            dates: r.start_date && r.end_date ? `${r.start_date} → ${r.end_date}` : '-',
            created: r.created_at
          })));
        }
      } else {
        if (!isAdminOrManager) updates.unreadLeaves = 0;
        setAssignedToMeLeaves([]);
      }
      if (formationsRes.ok) {
        const formationsData = await formationsRes.json();
        updates.unreadFormations = formationsData.unread_count || 0;
      }
      if (expensesRes?.ok) {
        const expensesData = await expensesRes.json();
        updates.unreadExpenses = expensesData.count ?? 0;
      }
      if (messagesRes?.ok) {
        const msgData = await messagesRes.json();
        if (msgData.count !== undefined) updates.unreadMessages = msgData.count;
      }
      if (ideasRes?.ok) {
        const ideasData = await ideasRes.json();
        updates.unreadIdeas = (ideasData.success && typeof ideasData.count === 'number') ? ideasData.count : 0;
      }
      // Idea Box badge: + unread "idea" notifications (so when anyone submits, others see badge until they open Idea Box)
      if (hasFeature(enabledFeatures, 'ideas', billingLoaded) && token) {
        try {
          const ideaNotifRes = await fetch(`${API_BASE}/api/notifications/unread-count?type=idea`, { headers: { Authorization: `Bearer ${token}` } });
          if (ideaNotifRes.ok) {
            const ideaNotifData = await ideaNotifRes.json();
            const ideaNotifCount = (ideaNotifData.success && typeof ideaNotifData.count === 'number') ? ideaNotifData.count : 0;
            updates.unreadIdeas = (updates.unreadIdeas || 0) + ideaNotifCount;
          }
        } catch (e) {}
      }
      if (formationsPendingRes?.ok) {
        const formationsPendingData = await formationsPendingRes.json();
        updates.pendingFormationRegistrations = (formationsPendingData.success && typeof formationsPendingData.count === 'number') ? formationsPendingData.count : 0;
      }
      // Documents badge: unread "document" notifications (when admin uploads for category/employee, they get notified)
      if (hasFeature(enabledFeatures, 'documents', billingLoaded) && token) {
        try {
          const docNotifRes = await fetch(`${API_BASE}/api/notifications/unread-count?type=document`, { headers: { Authorization: `Bearer ${token}` } });
          if (docNotifRes.ok) {
            const docNotifData = await docNotifRes.json();
            updates.unreadDocuments = (docNotifData.success && typeof docNotifData.count === 'number') ? docNotifData.count : 0;
          }
        } catch (e) {}
      }
      // Planning badge: unread "planning" notifications (shift assigned / change approved-rejected → selected employee only)
      if (hasFeature(enabledFeatures, 'planning', billingLoaded) && token) {
        try {
          const planNotifRes = await fetch(`${API_BASE}/api/notifications/unread-count?type=planning`, { headers: { Authorization: `Bearer ${token}` } });
          if (planNotifRes.ok) {
            const planNotifData = await planNotifRes.json();
            updates.unreadPlanning = (planNotifData.success && typeof planNotifData.count === 'number') ? planNotifData.count : 0;
          }
        } catch (e) {}
      }
      // Performance badge: unread "performance" notifications (evaluation assigned / 360 feedback → selected employee(s) only)
      if (hasFeature(enabledFeatures, 'performance', billingLoaded) && token) {
        try {
          const perfNotifRes = await fetch(`${API_BASE}/api/notifications/unread-count?type=performance`, { headers: { Authorization: `Bearer ${token}` } });
          if (perfNotifRes.ok) {
            const perfNotifData = await perfNotifRes.json();
            updates.unreadPerformance = (perfNotifData.success && typeof perfNotifData.count === 'number') ? perfNotifData.count : 0;
          }
        } catch (e) {}
      }

      if (Object.keys(updates).length > 0) {
        setStats(prev => ({ ...prev, ...updates }));
        if (setDashboardStats && (updates.leaveDays !== undefined || updates.hoursWorked !== undefined || updates.attendanceRate !== undefined)) {
          setDashboardStats(prev => ({
            ...prev,
            ...(updates.leaveDays !== undefined && { leaveDays: updates.leaveDays }),
            ...(updates.hoursWorked !== undefined && { hoursWorked: updates.hoursWorked }),
            ...(updates.attendanceRate !== undefined && { attendanceRate: updates.attendanceRate })
          }));
        }
      }
      // Live Chat badge: fetch unread total when feature enabled (badge on tile)
      if (hasFeature(enabledFeatures, 'live_chat', billingLoaded) && token) {
        fetch(`${API_BASE}/api/chat/unread-total`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(d => { if (d?.success && (typeof d.total === 'number' || typeof d.total === 'string')) setChatUnreadTotal(Number(d.total) || 0); })
          .catch(() => {});
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(dateLocale, { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(dateLocale, { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('home.good_morning') || 'Bonjour';
    if (hour < 18) return t('home.good_afternoon') || 'Bon après-midi';
    return t('home.good_evening') || 'Bonsoir';
  };

  const handleAppClick = (appId) => {
    const navigationMap = {
      'pointage': '/pointage',
      'historique': '/historique',
      'conges': '/conges',
      'calendrier': '/calendrier',
      'planning': '/planning',
      'ideas': '/ideas',
      'frais': '/notes-frais',
      'equipe': '/employees',
      'badges': '/badges',
      'performance': '/performance',
      'live-chat': '/live-chat',
      'stats': '/dashboard',
      'soldes': '/soldes',
      'formations': '/formations',
      'documents': '/documents',
      'messages': '/communiques',
      'rapports': '/rapports',
      'aide': '/aide',
      'profil': '/profil',
      'abonnements': '/facturation/plans',
      'facturation': '/facturation',
      'audit-photos': '/admin/audit-photos',
      'gps': '/admin/gps',
      'admin-panel': '/admin',
      'admin-dashboard': '/admin/dashboard',
      'super-admin': '/super-admin',
      'presence-traceability': '/rapports?reportType=presence_trace'
    };
    if (navigationMap[appId]) {
      const route = navigationMap[appId];
      // Utiliser React Router navigate (priorité)
      if (navigate) {
        navigate(route);
      } 
      // Fallback: utiliser onNavigate pour compatibilité (si pas de React Router)
      else if (onNavigate) {
        const { routeToTab } = require('../utils/navigation');
        const tab = routeToTab(route);
        onNavigate(tab);
      }
    }
  };

  const handleQuickAttendance = () => {
    if (onNavigate) {
      onNavigate('pointage-geo');
    }
  };

  // Palette de couleurs moderne
  const colorPalette = {
    pointage: { gradient: 'linear-gradient(135deg, #00d4ff, #00a8cc)', shadow: 'rgba(0, 212, 255, 0.3)' },
    historique: { gradient: 'linear-gradient(135deg, #4a90e2, #6bb3ff)', shadow: 'rgba(74, 144, 226, 0.3)' },
    conges: { gradient: 'linear-gradient(135deg, #00e676, #00b85c)', shadow: 'rgba(0, 230, 118, 0.3)' },
    calendrier: { gradient: 'linear-gradient(135deg, #667eea, #764ba2)', shadow: 'rgba(102, 126, 234, 0.3)' },
    frais: { gradient: 'linear-gradient(135deg, #ffc107, #ffd54f)', shadow: 'rgba(255, 193, 7, 0.3)' },
    equipe: { gradient: 'linear-gradient(135deg, #2196f3, #64b5f6)', shadow: 'rgba(33, 150, 243, 0.3)' },
    badges: { gradient: 'linear-gradient(135deg, #10b981, #34d399)', shadow: 'rgba(16, 185, 129, 0.3)' },
    stats: { gradient: 'linear-gradient(135deg, #3f51b5, #7986cb)', shadow: 'rgba(63, 81, 181, 0.3)' },
    soldes: { gradient: 'linear-gradient(135deg, #06b6d4, #22d3ee)', shadow: 'rgba(6, 182, 212, 0.3)' },
    formations: { gradient: 'linear-gradient(135deg, #009688, #4db6ac)', shadow: 'rgba(0, 150, 136, 0.3)' },
    planning: { gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)', shadow: 'rgba(99, 102, 241, 0.3)' },
    ideas: { gradient: 'linear-gradient(135deg, #fbbf24, #fcd34d)', shadow: 'rgba(251, 191, 36, 0.3)' },
    documents: { gradient: 'linear-gradient(135deg, #607d8b, #90a4ae)', shadow: 'rgba(96, 125, 139, 0.3)' },
    messages: { gradient: 'linear-gradient(135deg, #e91e63, #f06292)', shadow: 'rgba(233, 30, 99, 0.3)' },
    rapports: { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
    aide: { gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', shadow: 'rgba(99, 102, 241, 0.3)' },
    profil: { gradient: 'linear-gradient(135deg, #10b981, #34d399)', shadow: 'rgba(16, 185, 129, 0.3)' },
    abonnements: { gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', shadow: 'rgba(245, 158, 11, 0.3)' },
    facturation: { gradient: 'linear-gradient(135deg, #ec4899, #f472b6)', shadow: 'rgba(236, 72, 153, 0.3)' },
    'audit-photos': { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
    gps: { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', shadow: 'rgba(6, 182, 212, 0.3)' },
    'admin-panel': { gradient: 'linear-gradient(135deg, #1e40af, #3b82f6)', shadow: 'rgba(30, 64, 175, 0.3)' },
    performance: { gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', shadow: 'rgba(139, 92, 246, 0.3)' },
    live_chat: { gradient: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', shadow: 'rgba(14, 165, 233, 0.3)' },
    presence_traceability: { gradient: 'linear-gradient(135deg, #06b6d4, #22d3ee)', shadow: 'rgba(6, 182, 212, 0.3)' }
  };

  return (
    <div style={{
      minHeight: '100vh',
      height: '100%',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // overflowY: 'auto'
    }}>
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse-badge {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          50% { 
            transform: scale(1.1);
            box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
          }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        .home-app-icon {
          animation: slideUp 0.4s ease-out backwards;
        }
        .home-app-icon:nth-child(1) { animation-delay: 0.05s; }
        .home-app-icon:nth-child(2) { animation-delay: 0.1s; }
        .home-app-icon:nth-child(3) { animation-delay: 0.15s; }
        .home-app-icon:nth-child(4) { animation-delay: 0.2s; }
        .home-app-icon:nth-child(5) { animation-delay: 0.25s; }
        .home-app-icon:nth-child(6) { animation-delay: 0.3s; }
        .home-app-icon:nth-child(7) { animation-delay: 0.35s; }
        .home-app-icon:nth-child(8) { animation-delay: 0.4s; }
        .home-app-icon:nth-child(9) { animation-delay: 0.45s; }
        .home-app-icon:nth-child(10) { animation-delay: 0.5s; }
        .home-app-icon:nth-child(11) { animation-delay: 0.55s; }
        .home-app-icon:nth-child(12) { animation-delay: 0.6s; }
        .home-app-icon:nth-child(13) { animation-delay: 0.65s; }
        .home-app-icon:nth-child(14) { animation-delay: 0.7s; }
        .home-app-icon:nth-child(15) { animation-delay: 0.75s; }
        .home-app-icon:nth-child(16) { animation-delay: 0.8s; }
        .home-app-icon:nth-child(17) { animation-delay: 0.85s; }
        .home-app-icon:nth-child(18) { animation-delay: 0.9s; }
        .home-app-icon:nth-child(19) { animation-delay: 0.95s; }
        
        /* 🎯 OPTIMISATION MOBILE - Interface native iOS/Android */
        @media (max-width: 767px) {
          /* Grille mobile optimisée : 2 colonnes max pour meilleure lisibilité */
          .home-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            padding: 140px 16px 100px !important;
            gap: 16px !important;
          }
          
          /* Header mobile optimisé - réduire hauteur */
          .home-header {
            padding: 16px 16px 12px !important;
          }
          
          /* Titre header mobile - texte sur une ligne */
          .home-header > div:first-of-type {
            flex-wrap: nowrap !important;
            gap: 8px !important;
          }
          
          .home-header > div:first-of-type > div:first-of-type > div:first-of-type {
            font-size: clamp(16px, 4vw, 20px) !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          /* Cartes AppIcon optimisées pour mobile */
          .home-app-icon {
            min-height: 120px !important;
            padding: 20px 12px !important;
          }
          
          /* Icônes plus grandes sur mobile */
          .home-app-icon > div:first-of-type {
            font-size: 48px !important;
          }
          
          /* Labels plus lisibles sur mobile */
          .home-app-icon > div:last-of-type {
            font-size: 16px !important;
            font-weight: 600 !important;
            padding: 0 12px !important;
            line-height: 1.3 !important;
          }
          
          /* Cartes LargeWidget optimisées */
          .home-large-widget {
            padding: 28px !important;
            min-height: 140px !important;
          }
          
          /* Titre des LargeWidget plus grand */
          .home-large-widget > div:first-of-type > div:first-of-type {
            font-size: 20px !important;
            font-weight: 700 !important;
            margin-bottom: 8px !important;
          }
          
          /* Sous-titre des LargeWidget plus lisible */
          .home-large-widget > div:first-of-type > div:last-of-type {
            font-size: 16px !important;
            font-weight: 500 !important;
          }
          
          /* Icônes des LargeWidget plus grandes */
          .home-large-widget > div:last-of-type {
            font-size: 64px !important;
          }
          
          /* Badges de notification plus visibles */
          .home-app-icon [style*="background: linear-gradient(135deg, #ef4444"] {
            font-size: 12px !important;
            min-width: 24px !important;
            height: 24px !important;
            font-weight: 700 !important;
          }
          
          /* Dock navigation optimisé */
          .home-dock-icon {
            width: 72px !important;
            height: 72px !important;
          }
          
          .home-dock-icon > div:first-of-type {
            font-size: 28px !important;
          }
          
          .home-dock-icon > div:last-of-type {
            font-size: 12px !important;
            font-weight: 600 !important;
            margin-top: 6px !important;
          }
          
          /* StatBadge optimisé */
          .home-header > div:last-of-type > div {
            font-size: 14px !important;
            padding: 10px 18px !important;
          }
          
          /* Titre header réduit sur mobile */
          .home-header > div:first-of-type > div:first-of-type {
            font-size: clamp(18px, 4vw, 24px) !important;
            line-height: 1.2 !important;
          }
          
          /* Touch actions pour meilleure réactivité tactile */
          .home-app-icon,
          .home-large-widget,
          .home-dock-icon {
            touch-action: manipulation;
            -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
            cursor: pointer;
          }
          
          /* Amélioration de l'espacement entre éléments */
          .home-app-icon {
            gap: 16px !important;
          }
        }
        
        @media (min-width: 768px) {
          .home-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            padding: 200px 40px 120px !important;
            gap: 24px !important;
          }
          .home-app-icon {
            min-height: 120px !important;
          }
          .home-app-icon:hover {
            transform: translateY(-6px) scale(1.02) !important;
            box-shadow: 0 12px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2) !important;
          }
        }
        @media (min-width: 1024px) {
          .home-grid {
            grid-template-columns: repeat(5, 1fr) !important;
            padding: 200px 60px 120px !important;
            gap: 28px !important;
          }
          .home-app-icon {
            min-height: 140px !important;
          }
          .home-large-widget {
            min-height: 160px !important;
          }
        }
        @media (min-width: 1440px) {
          .home-grid {
            grid-template-columns: repeat(6, 1fr) !important;
            padding: 220px 100px 140px !important;
            gap: 32px !important;
          }
          .home-app-icon {
            min-height: 160px !important;
          }
          .home-large-widget {
            min-height: 180px !important;
            padding: 28px !important;
          }
        }
      `}</style>

      {/* Header: on mobile show minimal bar + drawer; on desktop show full header */}
      {isMobile ? (
        <>
          <div className="home-header" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            padding: '12px 16px',
            background: 'transparent',
            backdropFilter: 'none',
            borderBottom: 'none',
            zIndex: 999,
            maxWidth: '100%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {(user?.company_logo_url || user?.companyLogoUrl) && (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.3)',
                      flexShrink: 0,
                      background: 'rgba(15,23,42,0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={(() => {
                        const logo = user.company_logo_url || user.companyLogoUrl;
                        return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                      })()}
                      alt={user?.company || user?.companyName || 'Logo'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                )}
                <div style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '-0.5px', color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.company || user?.companyName || 'WorkBiFlow'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <NotificationIcon socket={notificationSocket} />
                <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Open menu'}
                title={t('home.open_menu') || 'Open menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  flexShrink: 0,
                  width: '44px',
                  height: '44px'
                }}
              >
                <PanelRightOpen size={22} aria-hidden />
              </button>
              </div>
            </div>
          </div>

          {/* Mobile drawer: top bar data */}
          {mobileDrawerOpen && (
            <div
              role="presentation"
              onClick={() => setMobileDrawerOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 2000,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t('common.menu') || 'Menu'}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: 'min(320px, 88vw)',
                  height: '100%',
                  background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
                  backdropFilter: 'blur(24px)',
                  borderLeft: '1px solid rgba(255,255,255,0.12)',
                  padding: '20px 16px',
                  boxSizing: 'border-box',
                  overflowY: 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ fontWeight: 700, fontSize: '16px', color: '#fff' }}>{t('common.menu') || 'Menu'}</span>
                  <button
                    type="button"
                    onClick={() => setMobileDrawerOpen(false)}
                    aria-label={t('common.close') || 'Close'}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getGreeting()} {user?.first_name || t('home.user') || 'Utilisateur'}
                  </div>
                  {(user?.company_logo_url || user?.companyLogoUrl) && (
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.3)',
                        flexShrink: 0,
                        background: 'rgba(15,23,42,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={(() => {
                          const logo = user.company_logo_url || user.companyLogoUrl;
                          return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                        })()}
                        alt={user?.company || user?.companyName || 'Logo'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  )}
                </div>
                {user?.company && (
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '12px' }}>
                    {user?.company || user?.companyName || '—'}
                  </div>
                )}
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>
                  {formatDate(currentTime)}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <LanguageSelector style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '6px 10px',
                    borderRadius: '10px',
                    backdropFilter: 'blur(10px)'
                  }} />
                  {onLogout && (
                    <button
                      onClick={() => { setMobileDrawerOpen(false); onLogout(); }}
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                        padding: '8px 14px',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <LogOut size={16} />
                      <span>{t('home.logout') || 'Déconnexion'}</span>
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <StatBadge icon={<Calendar size={16} />} value={`${Number(stats.leaveDays) || 0} ${t('home.paid_leave_badge')}`} isMobile={true} loading={statsLoading} />
                  <StatBadge icon={<Clock size={16} />} value={`${stats.hoursWorked}h`} isMobile={true} loading={statsLoading} />
                  <StatBadge icon="✓" value={`${stats.attendanceRate}%`} isMobile={true} loading={statsLoading} />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="home-header" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: '24px 20px 20px',
          background: 'transparent',
          backdropFilter: 'none',
          borderBottom: 'none',
          zIndex: 999,
          maxWidth: '100%'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '12px', flexWrap: 'nowrap' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {(user?.company_logo_url || user?.companyLogoUrl) && (
                <div
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.3)',
                    flexShrink: 0,
                    background: 'rgba(15,23,42,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={(() => {
                      const logo = user.company_logo_url || user.companyLogoUrl;
                      return /^https?:\/\//i.test(String(logo || '')) ? logo : `${API_BASE.replace(/\/$/, '')}/${logo}`;
                    })()}
                    alt={user?.company || user?.companyName || 'Logo'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, maxWidth: '100%', letterSpacing: '-0.5px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getGreeting()} {user?.first_name || t('home.user') || 'Utilisateur'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <NotificationIcon socket={notificationSocket} />
              <LanguageSelector style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', padding: '4px 8px', borderRadius: '10px', backdropFilter: 'blur(10px)' }} />
              {onLogout && (
                <button
                  onClick={onLogout}
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backdropFilter: 'blur(10px)',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <LogOut size={16} />
                  <span style={{ fontSize: '12px' }}>{t('home.logout') || 'Déconnexion'}</span>
                </button>
              )}
            </div>
          </div>
          {user?.company && (
            <div style={{ fontSize: 'clamp(14px, 2vw, 16px)', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.company || user?.companyName || '—'}
            </div>
          )}
          <div style={{ fontSize: 'clamp(14px, 2.5vw, 18px)', color: 'rgba(255, 255, 255, 0.7)', maxWidth: '100%', letterSpacing: '0.2px', fontWeight: 500, marginTop: '0' }}>
            {formatDate(currentTime)}
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '18px', flexWrap: 'wrap' }}>
            <StatBadge icon={<Calendar size={16} />} value={`${Number(stats.leaveDays) || 0} ${t('home.paid_leave_badge')}`} isMobile={false} loading={statsLoading} />
            <StatBadge icon={<Clock size={16} />} value={`${stats.hoursWorked}h`} isMobile={false} loading={statsLoading} />
            <StatBadge icon="✓" value={`${stats.attendanceRate}%`} isMobile={false} loading={statsLoading} />
          </div>
        </div>
      )}

      {/* Main App Grid */}
      <div className="home-grid" style={{
        // make the grid fill the available width like admin view and allow vertical scrolling
        padding: isMobile ? '72px 16px 100px' : '200px 40px 120px',
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
        gap: isMobile ? '16px' : '20px',

        maxWidth: 'none',
        width: '100%',
        margin: '0',
        minHeight: isMobile ? 'auto' : '100vh',
        alignContent: 'start',
        gridAutoRows: 'minmax(120px, auto)'
      }}
      data-mobile={isMobile}
      data-debug={`isMobile: ${isMobile}, width: ${typeof window !== 'undefined' ? window.innerWidth : 'N/A'}`}
      >
        {/* Super Admin: sirf Super Admin box; baaki modules band */}
        {!isSuperAdmin && (
        <>
        {!billingLoaded && (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px 20px',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '16px'
          }}>
            {t('common.loading') || 'Chargement des modules...'}
          </div>
        )}
        {/* 1. Pointage - sirf jab plan mein pointage ho */}
        {hasFeature(enabledFeatures, 'pointage', billingLoaded) && (
        <LargeWidget
          title={t('home.attendance') || 'Pointage'}
          subtitle={todayAttendance ? `${t('home.clock_in') || 'Entrée'} • ${new Date(todayAttendance.clockIn).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : t('home.click_to_clock') || 'Cliquez pour pointer'}
          icon={<Clock size={56} />}
          gradient={colorPalette.pointage.gradient}
          shadow={colorPalette.pointage.shadow}
          onClick={handleQuickAttendance}
          delay={0}
          isMobile={isMobile}
        />
        )}

        {/* 2. Historique */}
        {hasFeature(enabledFeatures, 'historique', billingLoaded) && (
        <AppIcon
          icon={<History size={36} />}
          label={t('home.history') || 'Historique'}
          color={colorPalette.historique.gradient}
          shadow={colorPalette.historique.shadow}
          onClick={() => handleAppClick('historique')}
          delay={1}
          isMobile={isMobile}
        />
        )}

        {/* 3. Congés */}
        {hasFeature(enabledFeatures, 'conges', billingLoaded) && (
        <AppIcon
          icon={<Calendar size={36} />}
          label={t('navigation.leaves') || 'Congés'}
          badge={stats.unreadLeaves}
          badgeTooltip={assignedToMeLeaves.length > 0 ? assignedToMeLeaves.map(r => `${r.employee_name || '?'} (${r.leave_type_name || ''})`).join(' • ') : null}
          color={colorPalette.conges.gradient}
          shadow={colorPalette.conges.shadow}
          onClick={() => handleAppClick('conges')}
          delay={2}
          isMobile={isMobile}
        />
        )}

        {/* 4. Formations - Admin/Manager: badge = pending registrations + new trainings not viewed (so notification = badge); Employee: new trainings not viewed */}
        {hasFeature(enabledFeatures, 'formations', billingLoaded) && (
        <AppIcon
          icon={<BookOpen size={36} />}
          label={t('formations.title') || 'Formation'}
          badge={['admin', 'manager'].includes(user?.role)
            ? (Number(stats.pendingFormationRegistrations) || 0) + (Number(stats.unreadFormations) || 0)
            : stats.unreadFormations}
          color={colorPalette.formations.gradient}
          shadow={colorPalette.formations.shadow}
          onClick={() => handleAppClick('formations')}
          delay={3}
          isMobile={isMobile}
        />
        )}

        {/* 5. Notes de Frais - badge = pending count sirf Admin ko (Manager ko nahi) */}
        {hasFeature(enabledFeatures, 'notes_frais', billingLoaded) && (
        <AppIcon
          icon={<DollarSign size={36} />}
          label={t('expenses.title') || 'Expense Reports'}
          badge={user?.role === 'admin' ? stats.unreadExpenses : 0}
          color={colorPalette.frais.gradient}
          shadow={colorPalette.frais.shadow}
          onClick={() => handleAppClick('frais')}
          delay={4}
        />
        )}

        {/* 6. Messages */}
        {hasFeature(enabledFeatures, 'communiques', billingLoaded) && (
        <AppIcon
          icon={<MessageSquare size={36} />}
          label={t('home.messages') || 'Messages'}
          badge={stats.unreadMessages}
          color={colorPalette.messages.gradient}
          shadow={colorPalette.messages.shadow}
          onClick={() => handleAppClick('messages')}
          delay={5}
        />
        )}

        {/* 7. Boîte à Idées - badge = new ideas (submitted) for Admin/Manager */}
        {hasFeature(enabledFeatures, 'ideas', billingLoaded) && (
        <AppIcon
          icon={<Lightbulb size={36} />}
          label={t('home.ideas_box') || 'Boîte à Idées'}
          badge={stats.unreadIdeas}
          color={colorPalette.ideas.gradient}
          shadow={colorPalette.ideas.shadow}
          onClick={() => handleAppClick('ideas')}
          delay={6}
          isMobile={isMobile}
        />
        )}

        {/* 8. Documents - badge = new document notifications for selected employee(s) or all (public) */}
        {hasFeature(enabledFeatures, 'documents', billingLoaded) && (
        <AppIcon
          icon={<File size={36} />}
          label={t('home.documents') || 'Documents'}
          badge={stats.unreadDocuments}
          color={colorPalette.documents.gradient}
          shadow={colorPalette.documents.shadow}
          onClick={() => handleAppClick('documents')}
          delay={7}
          isMobile={isMobile}
        />
        )}

        {/* 9. Planning - badge = unread planning notifications (shift assigned / change request → selected employee only) */}
        {hasFeature(enabledFeatures, 'planning', billingLoaded) && (
        <AppIcon
          icon={<Calendar size={36} />}
          label={t('navigation.planning') || 'Planning'}
          badge={stats.unreadPlanning}
          color={colorPalette.planning.gradient}
          shadow={colorPalette.planning.shadow}
          onClick={() => handleAppClick('planning')}
          delay={8}
          isMobile={isMobile}
        />
        )}

        {/* 11. Équipe - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<Users size={36} />}
            label="Équipe"
            color={colorPalette.equipe.gradient}
            shadow={colorPalette.equipe.shadow}
            onClick={() => handleAppClick('equipe')}
            delay={11}
            requiresAdmin={true}
          />
        )} */}

        {/* 12. Badges & QR - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<QrCode size={36} />}
            label="Badges & QR"
            color={colorPalette.badges.gradient}
            shadow={colorPalette.badges.shadow}
            onClick={() => handleAppClick('badges')}
            delay={12}
            requiresAdmin={true}
          />
        )} */}

        {/* 10. Calendrier */}
        {hasFeature(enabledFeatures, 'calendrier', billingLoaded) && (
        <AppIcon
          icon={<CalendarDays size={36} />}
          label={t('home.calendar') || 'Calendrier'}
          color={colorPalette.calendrier.gradient}
          shadow={colorPalette.calendrier.shadow}
          onClick={() => handleAppClick('calendrier')}
          delay={9}
          isMobile={isMobile}
        />
        )}

        {/* 11. Évaluations & Performance - badge = unread performance notifications (evaluation / 360 → selected employee(s) only) */}
        {hasFeature(enabledFeatures, 'performance', billingLoaded) && (
        <AppIcon
          icon={<Target size={36} />}
          label={t('home.performance') || 'Performance'}
          badge={stats.unreadPerformance}
          color={colorPalette.performance.gradient}
          shadow={colorPalette.performance.shadow}
          onClick={() => handleAppClick('performance')}
          delay={10}
          isMobile={isMobile}
        />
        )}

        {/* Live Chat (Enterprise) - badge when unread messages; disappears when seen in module */}
        {hasFeature(enabledFeatures, 'live_chat', billingLoaded) && (
        <AppIcon
          icon={<MessageCircle size={36} />}
          label={t('home.live_chat') || t('landing.plan_feature_live_chat') || 'Live Chat'}
          color={colorPalette.live_chat.gradient}
          shadow={colorPalette.live_chat.shadow}
          badge={chatUnreadTotal}
          onClick={() => handleAppClick('live-chat')}
          delay={10}
          isMobile={isMobile}
        />
        )}

        {/* 12. Profil & Team */}
        {hasFeature(enabledFeatures, 'profil', billingLoaded) && (
        <AppIcon
          icon={<User size={36} />}
          label={t('home.profile_team') || 'Profil & Team'}
          color={colorPalette.profil.gradient}
          shadow={colorPalette.profil.shadow}
          onClick={() => handleAppClick('profil')}
          delay={11}
        />
        )}

        {/* 14. Audit Photos - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<Camera size={36} />}
            label="Audit Photos"
            color={colorPalette['audit-photos'].gradient}
            shadow={colorPalette['audit-photos'].shadow}
            onClick={() => handleAppClick('audit-photos')}
            delay={15}
            requiresAdmin={true}
          />
        )} */}

        {/* 15. Facturation - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<CreditCard size={36} />}
            label="Facturation"
            color={colorPalette.facturation.gradient}
            shadow={colorPalette.facturation.shadow}
            onClick={() => handleAppClick('facturation')}
            delay={16}
            requiresAdmin={true}
          />
        )} */}

        {/* 17. Plans - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<Sparkles size={36} />}
            label="Plans"
            color={colorPalette.abonnements.gradient}
            shadow={colorPalette.abonnements.shadow}
            onClick={() => handleAppClick('abonnements')}
            delay={17}
            requiresAdmin={true}
          />
        )} */}

        {/* 18. Configuration GPS - Masqué (disponible dans Panneau Admin) */}
        {/* {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<MapPin size={36} />}
            label="Configuration GPS"
            color={colorPalette.gps.gradient}
            shadow={colorPalette.gps.shadow}
            onClick={() => handleAppClick('gps')}
            delay={user?.role === 'admin' ? 18 : 15}
            requiresAdmin={true}
          />
        )} */}

        {/* 20. Administration */}
        {['admin', 'manager'].includes(user?.role) && (
          <AppIcon
            icon={<Shield size={36} />}
            label={t('admin.title')}
            color={colorPalette['admin-panel'].gradient}
            shadow={colorPalette['admin-panel'].shadow}
            onClick={() => handleAppClick('admin-dashboard')}
            delay={20}
            requiresAdmin={true}
            isMobile={isMobile}
          />
        )}

        {/* Continuous Presence & Multi-site (company feature toggle by Super Admin) */}
        {['admin', 'manager'].includes(user?.role) &&
          hasFeature(enabledFeatures, 'reports', billingLoaded) &&
          hasFeature(enabledFeatures, 'presence_traceability', billingLoaded) && (
          <AppIcon
            icon={<Radio size={36} />}
            label={t('reports.type_presence_trace') || 'Continuous presence & multi-site'}
            color={colorPalette.presence_traceability.gradient}
            shadow={colorPalette.presence_traceability.shadow}
            onClick={() => handleAppClick('presence-traceability')}
            delay={21}
            requiresAdmin={true}
            isMobile={isMobile}
          />
        )}

        {/* 21. Aide */}
        <AppIcon
          icon={<HelpCircle size={36} />}
          label="Aide"
          color={colorPalette.aide.gradient}
          shadow={colorPalette.aide.shadow}
          onClick={() => handleAppClick('aide')}
          delay={user?.role === 'admin' ? 21 : 16}
        />

        </>
        )}

        {/* 20. Super Admin (sirf super admin ke liye - Dashboard/Nouveau hide) */}
        {isSuperAdmin && (
            <AppIcon
              icon={<Shield size={36} />}
              label="Super Admin"
              color={colorPalette['admin-panel'].gradient}
              shadow={colorPalette['admin-panel'].shadow}
              onClick={() => {
                handleAppClick('super-admin');
                window.history.pushState({}, '', '/super-admin');
              }}
              delay={0}
              requiresAdmin={true}
              isMobile={isMobile}
            />
        )}

        {/* 21. Aide - non-super-admin only (above block) */}
      </div>

      {/* Dock iOS 15+ Style - Super Admin: sirf Account & Me; Dashboard & Nouveau hide */}
      <div className="home-dock" style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '16px 20px',
        paddingBottom: `calc(16px + env(safe-area-inset-bottom, 0))`,
        background: 'transparent',
        backdropFilter: 'none',
        borderTop: 'none',
        borderRadius: '0',
        display: 'flex',
        justifyContent: 'space-around',
        gap: '12px',
        maxWidth: '100%',
        boxShadow: 'none'
      }}>
        <DockIcon icon={<Home size={24} />} label={isSuperAdmin ? (t('account') || 'Account') : t('navigation.home')} active onClick={() => {}} isMobile={isMobile} />
        {!isSuperAdmin && (
          <>
            {/* Dashboard: admin/manager = company-wide; employee = personal dashboard (when plan has feature) */}
            {['admin', 'manager', 'employee'].includes(user?.role) && hasFeature(enabledFeatures, 'dashboard', billingLoaded) && (
              <DockIcon icon={<BarChart3 size={24} />} label="Dashboard" onClick={() => handleAppClick('stats')} isMobile={isMobile} />
            )}
            <DockIcon icon={<Plus size={24} />} label={t('common.add')} onClick={handleQuickAttendance} isMobile={isMobile} />
          </>
        )}
        <DockIcon icon={<User size={24} />} label={isSuperAdmin ? (t('me') || 'Me') : t('common.profile')} onClick={() => handleAppClick('profil')} isMobile={isMobile} />
      </div>
    </div>
  );
};

// Composant StatBadge avec Glassmorphism
const StatBadge = ({ icon, value, isMobile = false, loading = false }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: isMobile ? '14px' : '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    lineHeight: '1.3',
    padding: isMobile ? '10px 18px' : '8px 16px'
  }}>
    {typeof icon === 'string' ? <span style={{ fontSize: '16px' }}>{icon}</span> : icon}
    {loading ? (
      <span
        aria-label="loading"
        style={{
          width: isMobile ? 14 : 12,
          height: isMobile ? 14 : 12,
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.5)',
          borderTopColor: 'transparent',
          animation: 'spin 0.8s linear infinite'
        }}
      />
    ) : (
      <span>{value}</span>
    )}
  </div>
);

// Composant LargeWidget
const LargeWidget = ({ title, subtitle, icon, gradient, shadow, onClick, delay = 0, requiresAdmin = false, isMobile = false }) => (
  <div
    className="home-app-icon home-large-widget"
    onClick={onClick}
    style={{
      gridColumn: 'span 2',
      aspectRatio: '2/1',
      background: gradient,
      borderRadius: '24px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '28px' : '24px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `0 8px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)`,
      animation: `slideUp 0.4s ease-out ${delay * 0.05}s backwards`,
      willChange: 'transform',
      minHeight: isMobile ? '140px' : 'auto',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
    }}
    onMouseDown={(e) => {
      e.currentTarget.style.transform = 'scale(0.96)';
    }}
    onMouseUp={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'scale(1)';
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
      e.currentTarget.style.boxShadow = `0 12px 40px ${shadow}, 0 6px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)`;
    }}
  >
    <div style={{ position: 'relative', zIndex: 2 }}>
      <div style={{ fontSize: isMobile ? '20px' : '18px', fontWeight: 700, marginBottom: '6px', letterSpacing: '-0.3px' }}>
        {title}
      </div>
      <div style={{ fontSize: isMobile ? '16px' : '13px', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
        {subtitle}
      </div>
    </div>
    <div style={{ 
      fontSize: isMobile ? '64px' : '56px', 
      position: 'relative', 
      zIndex: 2,
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
      opacity: 0.95
    }}>
      {icon}
    </div>
    {/* Overlay pour effet de profondeur */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
      pointerEvents: 'none',
      zIndex: 1
    }} />
    {/* Icône décorative en arrière-plan */}
    <div style={{
      position: 'absolute',
      bottom: '-20px',
      right: '-20px',
      fontSize: '120px',
      opacity: 0.1,
      transform: 'rotate(-15deg)',
      zIndex: 0
    }}>
      {icon}
    </div>
  </div>
);

// Composant AppIcon amélioré
const AppIcon = ({ icon, label, badge, badgeTooltip, color, shadow, pulse = false, onClick, delay = 0, requiresAdmin = false, isMobile = false }) => {
  return (
    <div
      className="home-app-icon"
      onClick={onClick}
      style={{
        aspectRatio: '1',
        background: color || '#1e293b',
        borderRadius: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '16px' : '10px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: shadow ? `0 4px 16px ${shadow}, 0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)` : '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
        animation: `slideUp 0.4s ease-out ${delay * 0.05}s backwards`,
        willChange: 'transform',
        minHeight: isMobile ? '120px' : '100px',
        padding: isMobile ? '20px 12px' : undefined,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
      }}
      data-mobile={isMobile}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.92)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
        e.currentTarget.style.boxShadow = shadow ? `0 12px 32px ${shadow}, 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)` : '0 12px 32px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)';
      }}
    >
      {/* Overlay glassmorphism */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />
      {/* Icon */}
      <div style={{
        fontSize: isMobile ? '48px' : '44px',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
      {/* Badge on top-right of card (WhatsApp-style), above overlay - not clipped */}
      {badge != null && Number(badge) > 0 && (
        <div
          title={badgeTooltip || undefined}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: 'white',
            fontSize: isMobile ? '12px' : '11px',
            fontWeight: 700,
            minWidth: isMobile ? '22px' : '20px',
            height: isMobile ? '22px' : '20px',
            borderRadius: isMobile ? '11px' : '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            boxShadow: '0 2px 8px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.25)',
            border: '2px solid rgba(255,255,255,0.95)',
            zIndex: 10,
            animation: pulse ? 'pulse-badge 2s ease-in-out infinite' : 'none'
          }}
        >
          {Number(badge) > 99 ? '99+' : Number(badge)}
        </div>
      )}
      <div style={{
        fontSize: isMobile ? '16px' : '12px',
        fontWeight: 600,
        textAlign: 'center',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: isMobile ? '0 12px' : '0 8px',
        position: 'relative',
        zIndex: 2,
        letterSpacing: '0.1px',
        lineHeight: '1.3'
      }}>
        {label}
      </div>
    </div>
  );
};

// Composant DockIcon amélioré
const DockIcon = ({ icon, label, active = false, onClick, isMobile = false }) => {
  return (
    <div
      className="home-dock-icon"
      onClick={onClick}
      style={{
        width: isMobile ? '72px' : '64px',
        height: isMobile ? '72px' : '64px',
        background: active ? 'linear-gradient(135deg, #00d4ff, #00a8cc)' : 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(10px)',
        borderRadius: '18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: active ? '1px solid rgba(0, 212, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: active ? '0 4px 16px rgba(0, 212, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.2)',
        position: 'relative',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.1)'
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'scale(0.85)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
        if (!active) {
          e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
        }
      }}
    >
      {active && (
        <div style={{
          position: 'absolute',
          top: '-4px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '4px',
          height: '4px',
          background: '#00d4ff',
          borderRadius: '2px',
          boxShadow: '0 0 8px rgba(0, 212, 255, 0.6)'
        }} />
      )}
      <div style={{ 
        fontSize: isMobile ? '28px' : '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
      }}>
        {icon}
      </div>
      <div style={{ 
        fontSize: isMobile ? '12px' : '10px', 
        marginTop: '4px', 
        fontWeight: 600, 
        letterSpacing: '0.1px',
        lineHeight: '1.3'
      }}>
        {label}
      </div>
    </div>
  );
};

export default HomePage;
